/**
 * BaseSection component for managing the warband's home base.
 */

import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Input,
  Label,
} from '@/components/ui';
import { Home } from 'lucide-react';
import { cn } from '@/utils';
import {
  useWarbandStore,
  selectBase,
  selectBaseNotes,
} from '@/store/warbandSlice';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function BaseSection() {
  // Store
  const base = useWarbandStore(selectBase);
  const baseNotes = useWarbandStore(selectBaseNotes);
  const setBase = useWarbandStore((state) => state.setBase);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleNameChange = (name: string) => {
    setBase(name || undefined, baseNotes);
  };

  const handleNotesChange = (notes: string) => {
    setBase(base, notes || undefined);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Home className="h-5 w-5 text-primary" />
          <CardTitle>Home Base</CardTitle>
        </div>
        <CardDescription>
          Your warband's headquarters in Frostgrave
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="base-name">Base Name</Label>
          <Input
            id="base-name"
            value={base || ''}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="e.g., The Frozen Tower, Ruined Inn..."
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="base-notes">Notes & Upgrades</Label>
          <textarea
            id="base-notes"
            value={baseNotes || ''}
            onChange={(e) => handleNotesChange(e.target.value)}
            placeholder="Record base upgrades, features, and notes..."
            rows={3}
            className={cn(
              'flex w-full rounded-md border border-input bg-background px-3 py-2',
              'text-sm placeholder:text-muted-foreground',
              'focus-visible:outline-none focus-visible:ring-2',
              'focus-visible:ring-ring focus-visible:ring-offset-2'
            )}
          />
        </div>
      </CardContent>
    </Card>
  );
}

